import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ImageAssetType } from '../model/AssetsManagement';
import type { ImageModelCaches } from '@/components/ui/carousel/carousel.types';
import { reorderByPhotoUUID } from '../edit/reorderList';
import { orderByAssetUUIDs } from '../edit/applyServerOrder';
import {
  isInQueryScope,
  isPhotoInView,
  isSelectableStagedAsset,
  type PhotoPool
} from '../model/assetViewScope';
import { readShowImageDetails } from './imageDetailsPreference';

// DV-2759 UI follow-up: dirtiness must reflect a DIFF from the last-synced
// baseline, not the mere fact a field was touched. Without this, a reverting
// edit (hide then unhide, a caption typed back to its original) left dirty /
// dirtyPms set, so the unsynced-changes badge stayed lit until a page refresh
// wiped session state and the (correct) backend-derived flag took over.
//
// Two kinds of contribution, combined here:
//   - Reversible edits — hide and caption — diffed against the baseline, so
//     returning to the original nets to clean. `hidden` is diffed PER CHANNEL
//     (webhook baseline vs pms baseline) because a hide is owed to both and they
//     clear independently; caption is webhook-only. Reorder can't be diffed
//     per-item, so it's the orderDirty touch-flag. The `!== undefined` guards
//     mean an item with no baseline (session-new) contributes nothing here — its
//     dirtiness comes entirely from the content flags below.
//   - Sticky content — uploads, staged commits, regenerate — which an
//     in-session edit can't undo. Tracked per-channel (webhook/pms) and OR-ed
//     in so a later reversible edit can't wipe a pending upload/regenerate
//     (and so caption never lights the PMS channel).
//   - Reorder is owed to both channels (DV-2799): orderDirty for the webhook,
//     pmsOrderDirty for the PMS, each cleared by its own sync.
function recomputeDirtyFlags(image: ImageAssetType): void {
  const captionChanged =
    image.originalCaption !== undefined &&
    (image.caption ?? '') !== image.originalCaption;
  const hideChangedWebhook =
    image.originalHidden !== undefined &&
    Boolean(image.hidden) !== image.originalHidden;
  const hideChangedPms =
    image.pmsBaselineHidden !== undefined &&
    Boolean(image.hidden) !== image.pmsBaselineHidden;
  image.dirtyPms =
    hideChangedPms ||
    image.pmsOrderDirty === true ||
    image.pmsContentDirty === true;
  image.dirty =
    captionChanged ||
    hideChangedWebhook ||
    image.orderDirty === true ||
    image.webhookContentDirty === true;
}

export type ImageAssetState = {
  imageAssets: ImageAssetType[];
  // View flag: when false (default) the grid hides `hidden` photos and
  // select-all skips them.
  showHidden: boolean;
  // DV-2856 view flags. `showImageDetails` is seeded from (and written back to)
  // localStorage — see imageDetailsPreference.ts and useImageDetailsToggle.
  // The write-back lives in the hook, NOT here: reducers stay pure.
  showImageDetails: boolean;
  // Grid search. Deliberately not persisted — a filter that survives a reload
  // reads as "half my photos are missing".
  searchQuery: string;
  // DV-3735: the active tab's photos — every photo, or the floor plans only.
  // The page decides it from the tab and writes it here (setPhotoPool), so
  // every reader of the view filters gets it with them rather than through a
  // prop each consumer could forget.
  pool: PhotoPool;
};

const initialState: ImageAssetState = {
  imageAssets: [],
  showHidden: false,
  showImageDetails: readShowImageDetails(),
  searchQuery: '',
  pool: 'all'
};

// "All" means "all currently displayed" (DV-2856 spec section 3.3): hidden
// photos were already excluded from that pool, and a search narrows it the same
// way. Both select-all reducers below take their pool from model/assetViewScope
// — the same module the grids, the counts and the bulk actions read — so the
// header checkbox and the visible cards can never disagree about it.
// ImageAssetState carries `showHidden`, `searchQuery` and `pool`, i.e. it IS an
// AssetViewFilters; passing the state itself keeps that fact in one place.

export const imageAssetSlice = createSlice({
  name: 'imageAsset',
  initialState,
  reducers: {
    setImageAssets: (state, action: PayloadAction<ImageAssetType[]>) => {
      state.imageAssets = action.payload.map((image) => ({
        ...image,
        isChecked: false,
        // Server data is the synced baseline (DV-2759 UI follow-up): capture it
        // and start clean, so a later edit is measured as a diff from here. Both
        // channel baselines for `hidden` start equal to the loaded state.
        originalHidden: Boolean(image.hidden),
        pmsBaselineHidden: Boolean(image.hidden),
        originalCaption: image.caption ?? '',
        orderDirty: false,
        pmsOrderDirty: false,
        webhookContentDirty: false,
        pmsContentDirty: false,
        dirty: false,
        dirtyPms: false
      }));
      // A different asset set just loaded (photoset switch, property change):
      // a query typed against the old one is stale, and leaving it on would
      // silently filter the new set. Selections are already reset above.
      state.searchQuery = '';
    },
    toggleAllImageAssets: (state) => {
      // Select-all operates on what the grid shows: hidden photos are skipped
      // unless Show Hidden is on, and non-matching photos are skipped while a
      // search is active. (With neither in play this is exactly the old
      // everything-toggles behavior.) The pool narrows it too (DV-3735): the
      // Floor Plan tab's Select All checks floor plans only.
      const visible = state.imageAssets.filter((image) =>
        isPhotoInView(image, state)
      );
      if (visible.length === 0) return;
      const shouldSelectAll = !visible.every((image) => image.isChecked);
      state.imageAssets = state.imageAssets.map((image) =>
        isPhotoInView(image, state)
          ? { ...image, isChecked: shouldSelectAll }
          : image
      );
    },
    // Virtual Staging tab select-all: scoped strictly to the staged, non-hidden
    // subset — the Photos tab's selection (non-staged photos) is left as-is.
    toggleAllStagedAssets: (state) => {
      const staged = state.imageAssets.filter((image) =>
        isSelectableStagedAsset(image, state)
      );
      if (staged.length === 0) return;
      const shouldSelectAll = !staged.every((image) => image.isChecked);
      state.imageAssets = state.imageAssets.map((image) =>
        isSelectableStagedAsset(image, state)
          ? { ...image, isChecked: shouldSelectAll }
          : image
      );
    },
    // Product decision (PR #1057 review Note 1): selection is per-tab-visit,
    // not per-photo — switching between the Photos and Virtual Staging tabs
    // clears ALL checkmarks so an action on one tab can never pick up
    // selections made on the other.
    clearAllSelections: (state) => {
      state.imageAssets.forEach((image) => {
        image.isChecked = false;
      });
    },
    toggleImageAsset: (state, action: PayloadAction<string>) => {
      const photoUUID = action.payload;
      state.imageAssets = state.imageAssets.map((image) =>
        image.photoUUID === photoUUID
          ? { ...image, isChecked: !image.isChecked }
          : image
      );
    },
    resetImageAssets: (state) => {
      state.imageAssets = [];
    },
    // Exact rollback restore (failed reorder persist): unlike setImageAssets
    // this keeps isChecked/dirty/hidden verbatim from the captured snapshot.
    restoreImageAssets: (state, action: PayloadAction<ImageAssetType[]>) => {
      state.imageAssets = action.payload;
    },
    // A successful Sync webhook push means everything local is now
    // synced — mirror the backend clearing hasUnsyncedChanges.
    clearDirtyFlags: (state) => {
      state.imageAssets.forEach((image) => {
        image.dirty = false;
        image.dirtyPms = false;
        image.orderDirty = false;
        image.pmsOrderDirty = false;
        // The webhook push delivered everything, so all pending content is now
        // synced on both channels and the current state IS the new baseline —
        // clear the content flags and re-seed BOTH channel baselines (DV-2759
        // UI). This also graduates session-new items (uploads/staged) to clean.
        image.webhookContentDirty = false;
        image.pmsContentDirty = false;
        image.originalHidden = Boolean(image.hidden);
        image.pmsBaselineHidden = Boolean(image.hidden);
        image.originalCaption = image.caption ?? '';
      });
    },
    // A successful direct PMS sync only delivers PMS-deliverable edits
    // (uploads, hides/unhides, reorder since DV-2799) — captions never reach
    // the PMS, so `dirty` (the webhook-owed flag) must stay as-is. Only
    // `dirtyPms` is cleared here.
    clearPmsDirtyFlags: (state) => {
      state.imageAssets.forEach((image) => {
        image.dirtyPms = false;
        // The PMS sync delivered PMS-owed edits (hide/unhide, upload/regenerate,
        // order), so re-seed the PMS-side baselines only — NOT the webhook twins, which
        // caption/reorder and the un-pushed content still owe. This is what makes
        // a post-PMS-sync unhide correctly re-light dirtyPms (the PMS deleted it,
        // it now owes a restore) while a post-sync caption edit does not
        // (DV-2759 UI review); a post-sync reorder does, through pmsOrderDirty
        // (DV-2799). Content flags: clear the PMS one, keep the webhook one.
        image.pmsBaselineHidden = Boolean(image.hidden);
        image.pmsContentDirty = false;
        image.pmsOrderDirty = false;
      });
    },
    addUploaded: (state, action: PayloadAction<ImageAssetType[]>) => {
      // Idempotent by assetUUID: the upload modal can hand over a photo that is
      // already in the grid (a cancel's leftovers after a refetch or a confirm).
      const present = new Set(state.imageAssets.map((image) => image.assetUUID));
      const fresh = action.payload.filter((image) => !present.has(image.assetUUID));
      state.imageAssets.push(
        // A fresh upload is itself an un-synced change — it needs a Sync &
        // Share pass to reach the PMS/listing, same as any edit (per product
        // Q&A #2). It's also PMS-deliverable: the direct PMS sync can push a
        // new photo up.
        ...fresh.map((image) => ({
          ...image,
          isChecked: false,
          // Sticky new content on both channels (not a reversible edit).
          webhookContentDirty: true,
          pmsContentDirty: true,
          dirty: true,
          dirtyPms: true
        }))
      );
    },
    addStaged: (
      state,
      action: PayloadAction<{
        originalPhotoUUID: string;
        staged: ImageAssetType;
      }>
    ) => {
      const { originalPhotoUUID, staged } = action.payload;
      // Idempotence guard: a duplicate approve (e.g. a double-submit of the
      // commit action) must not insert the same staged item twice.
      const alreadyInserted = state.imageAssets.some(
        (image) => image.photoUUID === staged.photoUUID
      );
      if (alreadyInserted) return;
      // The staged photo is the commit's only edit — an upload, so it's an
      // unsynced change on both channels (needs a Sync pass; the
      // direct PMS sync can deliver it too). The ORIGINAL is untouched: per
      // the DV-2142 AC update (Ops request), the backend no longer hides it
      // on commit — the staged photo sits alongside it.
      const stagedItem = {
        ...staged,
        isChecked: false,
        // Sticky new content on both channels (not a reversible edit).
        webhookContentDirty: true,
        pmsContentDirty: true,
        dirty: true,
        dirtyPms: true
      };
      const index = state.imageAssets.findIndex(
        (image) => image.photoUUID === originalPhotoUUID
      );
      if (index < 0) {
        state.imageAssets.push(stagedItem);
        return;
      }
      state.imageAssets.splice(index + 1, 0, stagedItem);
    },
    // DV-2252 regenerate-after-staged: a re-commit REPLACES the committed
    // asset server-side (same assetUUID/photoUUID, new image). Mirror that
    // here: swap the caches + settings on the existing item and mark it
    // unsynced on both channels, exactly like any other edit.
    updateStaged: (
      state,
      action: PayloadAction<{
        photoUUID: string;
        caches: ImageModelCaches;
        stagedRoomType?: string;
        stagedStyle?: string;
      }>
    ) => {
      const item = state.imageAssets.find(
        (image) => image.photoUUID === action.payload.photoUUID
      );
      if (!item) return;
      item.caches = action.payload.caches;
      if (action.payload.stagedRoomType) {
        item.stagedRoomType = action.payload.stagedRoomType;
      }
      if (action.payload.stagedStyle) {
        item.stagedStyle = action.payload.stagedStyle;
      }
      // A regenerate replaces the image content in place. It's sticky new
      // content on both channels — set the content flags (not dirty/dirtyPms
      // directly) and recompute, so a subsequent hide→unhide / reorder can't
      // wipe the still-unsynced regenerate (the item is already baselined).
      item.webhookContentDirty = true;
      item.pmsContentDirty = true;
      recomputeDirtyFlags(item);
    },
    setShowHidden: (state, action: PayloadAction<boolean>) => {
      state.showHidden = action.payload;
    },
    setShowImageDetails: (state, action: PayloadAction<boolean>) => {
      state.showImageDetails = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setPhotoPool: (state, action: PayloadAction<PhotoPool>) => {
      state.pool = action.payload;
    },
    setCaption: (
      state,
      action: PayloadAction<{ photoUUID: string; caption: string }>
    ) => {
      const image = state.imageAssets.find(
        (i) => i.photoUUID === action.payload.photoUUID
      );
      if (image && (image.caption ?? '') !== action.payload.caption) {
        image.caption = action.payload.caption;
        // Diff against the load baseline: typing a caption back to its original
        // clears the flag instead of leaving it stuck dirty (DV-2759 UI).
        recomputeDirtyFlags(image);
        // The search and the Floor Plan pool both read the caption, so a rename
        // can take a checked photo out of the view. It leaves the selection
        // with it, as a hidden photo does in toggleHide: a check the grid no
        // longer shows would hold the filter lock with no way to clear it, and
        // label the toolbar "Download Selected" over a download of everything.
        if (!isInQueryScope(image, state)) image.isChecked = false;
      }
    },
    toggleHide: (state, action: PayloadAction<string>) => {
      const image = state.imageAssets.find(
        (i) => i.photoUUID === action.payload
      );
      if (!image) return;
      image.hidden = !image.hidden;
      // Diff against the load baseline: an unhide back to the original state
      // clears the flags instead of re-marking dirty (DV-2759 UI — the reported
      // bug). Hide/unhide is PMS-deliverable in both directions: Buildium/
      // AppFolio (not Rentvine) delete/restore hidden synced photos on sync.
      recomputeDirtyFlags(image);
      // A photo that just became hidden must leave the selection, or it
      // would remain invisibly counted in download/sync/order selections.
      if (image.hidden) image.isChecked = false;
    },
    hideMany: (state, action: PayloadAction<string[]>) => {
      const targets = new Set(action.payload);
      state.imageAssets.forEach((image) => {
        if (targets.has(image.photoUUID) && !image.hidden) {
          image.hidden = true;
          recomputeDirtyFlags(image);
          image.isChecked = false;
        }
      });
    },
    // DV-2524: optimistic removal for a trash. The photo leaves the working
    // set entirely — with it goes its dirty flag, which is why the header note
    // falls back to uncounted "Unsynced changes" copy after a delete (the
    // photoset's backend flags carry the pending change).
    removeAssets: (state, action: PayloadAction<string[]>) => {
      const targets = new Set(action.payload);
      state.imageAssets = state.imageAssets.filter(
        (image) => !targets.has(image.photoUUID)
      );
    },
    // Rollback for a failed trash: puts one photo back where the user last saw
    // it, flags and selection verbatim. The payload is the photoUUID order as it
    // stood BEFORE the optimistic removal, not an index: an index captured
    // pre-removal and applied post-removal lands the photo too far right by the
    // number of earlier-removed siblings, and the error varies with the order
    // the requests fail in. Instead, insert before the first photo still present
    // that followed this one in the snapshot; if none survive, it belonged at the
    // end. Per-photo rather than a whole-list snapshot restore so a bulk delete
    // with one failure cannot clobber an edit made while the requests were in
    // flight. Idempotent — a repeated rollback of an already-restored photo is a
    // no-op.
    reinsertAsset: (
      state,
      action: PayloadAction<{ image: ImageAssetType; orderBefore: string[] }>
    ) => {
      const { image, orderBefore } = action.payload;
      if (state.imageAssets.some((i) => i.photoUUID === image.photoUUID)) return;
      const wasAt = orderBefore.indexOf(image.photoUUID);
      // Not in the snapshot at all (should not happen — the caller captures from
      // the same list): append rather than guess a position.
      if (wasAt === -1) {
        state.imageAssets.push(image);
        return;
      }
      const insertAt = state.imageAssets.findIndex(
        (current) => orderBefore.indexOf(current.photoUUID) > wasAt
      );
      if (insertAt === -1) {
        state.imageAssets.push(image);
        return;
      }
      state.imageAssets.splice(insertAt, 0, image);
    },
    // A recovered photo re-enters at the END of the sequence, mirroring the
    // backend's max(sortOrder) + 1. It is an unsynced change on both channels
    // (it re-enters the deliverable set), same as a fresh upload.
    addRecovered: (state, action: PayloadAction<ImageAssetType>) => {
      const image = action.payload;
      if (state.imageAssets.some((i) => i.photoUUID === image.photoUUID)) return;
      state.imageAssets.push({
        ...image,
        isChecked: false,
        // Sticky new content on both channels (re-enters the deliverable set).
        webhookContentDirty: true,
        pmsContentDirty: true,
        dirty: true,
        dirtyPms: true
      });
    },
    reorderAssets: (
      state,
      action: PayloadAction<{
        fromPhotoUUID: string;
        toPhotoUUID: string;
        position: 'before' | 'after';
      }>
    ) => {
      const { fromPhotoUUID, toPhotoUUID, position } = action.payload;
      const next = reorderByPhotoUUID(
        state.imageAssets,
        fromPhotoUUID,
        toPhotoUUID,
        position
      );
      if (next === state.imageAssets) return;
      state.imageAssets = next;
      const moved = state.imageAssets.find(
        (i) => i.photoUUID === fromPhotoUUID
      );
      if (moved) {
        // Reorder can't be diffed per-item (order is array position, not a
        // per-item sortOrder), so track it as a touch-flag that recomputeDirty
        // OR-s into `dirty`. DV-2799: the backend now owes a reorder to the
        // PMS too, so pmsOrderDirty lights dirtyPms the same way. A same-slot
        // drop returns early above and marks nothing. Reorder-revert netting
        // is a separate follow-up.
        moved.orderDirty = true;
        moved.pmsOrderDirty = true;
        recomputeDirtyFlags(moved);
      }
    },
    // DV-2859 Caption Sort: adopt the order the server returned. Unlike
    // reorderAssets this is not one move but a whole permutation, so every
    // photo whose position changed gets the order touch-flags (both channels,
    // same as a drag — DV-2799). An already-sorted set is a same-reference
    // no-op — no flag is raised, so the unsynced badge stays dark (AC).
    applyPhotosetOrder: (state, action: PayloadAction<string[]>) => {
      const before = state.imageAssets;
      const next = orderByAssetUUIDs(before, action.payload);
      if (next === before) return;
      next.forEach((image, index) => {
        if (image !== before[index]) {
          image.orderDirty = true;
          image.pmsOrderDirty = true;
          recomputeDirtyFlags(image);
        }
      });
      state.imageAssets = next;
    }
  }
});

export const {
  setImageAssets,
  toggleAllImageAssets,
  toggleAllStagedAssets,
  clearAllSelections,
  toggleImageAsset,
  resetImageAssets,
  restoreImageAssets,
  clearDirtyFlags,
  clearPmsDirtyFlags,
  addUploaded,
  addStaged,
  updateStaged,
  setShowHidden,
  setShowImageDetails,
  setSearchQuery,
  setPhotoPool,
  setCaption,
  toggleHide,
  hideMany,
  removeAssets,
  reinsertAsset,
  addRecovered,
  reorderAssets,
  applyPhotosetOrder
} = imageAssetSlice.actions;

export default imageAssetSlice.reducer;
